import React, { useCallback, useEffect, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.css";
import "../../../assets/scss/datepicker.css";
import { leaveManagamentPlaceholder } from "services/constants/PlaceHolder";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { TextField, InputAdornment, IconButton } from "@material-ui/core";

const BSDateRangePicker = ({
  handleStartDate,
  showToday,
  triggerDate,
  includeCurrentDate,
  fromDate,
  toDate,
  allowFutureDate = false,
}) => {
  const resolveRangeLabel = useCallback((startMoment, endMoment) => {
    const normalizedStart = startMoment.clone().startOf("day");
    const normalizedEnd = endMoment.clone().startOf("day");

    const todayRange = [
      moment().clone().startOf("day"),
      moment().clone().startOf("day"),
    ];
    const yesterdayRange = [
      moment().clone().subtract(1, "days").startOf("day"),
      moment().clone().subtract(1, "days").startOf("day"),
    ];
    const last7DaysRange = includeCurrentDate
      ? [
          moment().clone().subtract(6, "days").startOf("day"),
          moment().clone().startOf("day"),
        ]
      : [
          moment().clone().subtract(7, "days").startOf("day"),
          moment().clone().subtract(1, "days").startOf("day"),
        ];
    const last30DaysRange = [
      moment().clone().subtract(30, "days").startOf("day"),
      moment().clone().subtract(1, "days").startOf("day"),
    ];
    const thisMonthRange = [
      moment().clone().startOf("month"),
      moment().clone().endOf("month").startOf("day"),
    ];
    const lastMonthRange = [
      moment().clone().subtract(1, "month").startOf("month"),
      moment().clone().subtract(1, "month").endOf("month").startOf("day"),
    ];

    const matchesRange = ([expectedStart, expectedEnd]) =>
      normalizedStart.isSame(expectedStart, "day") &&
      normalizedEnd.isSame(expectedEnd, "day");

    if (showToday && matchesRange(todayRange)) return "Today";
    if (matchesRange(yesterdayRange)) return "Yesterday";
    if (matchesRange(last7DaysRange)) return "Last 7 Days";
    if (matchesRange(last30DaysRange)) return "Last 30 Days";
    if (matchesRange(thisMonthRange)) return "This Month";
    if (matchesRange(lastMonthRange)) return "Last Month";

    return "Custom Range";
  }, [includeCurrentDate, showToday]);

  const [state, setState] = useState(() => {
    if (fromDate && toDate) {
      return {
        start: moment(fromDate, "DD/MM/YYYY"),
        end: moment(toDate, "DD/MM/YYYY"),
      };
    }
    if (!triggerDate) {
      const today = moment();
      return { start: today.clone(), end: today.clone() };
    } else {
      // When triggerDate is true and includeCurrentDate is requested,
      // default to the current date (today) instead of the last 7 days.
      if (includeCurrentDate) {
        const today = moment();
        return { start: today.clone(), end: today.clone() };
      }
      // Fallback: keep the previous behavior (last 7 days excluding today)
      return {
        start: moment().subtract(7, "days"),
        end: moment().subtract(1, "days"),
      };
    }
  });
  const [trigger, setTrigger] = useState(true);
  const [labelName, setLabelName] = useState(
    triggerDate ? (includeCurrentDate ? "Today" : "Last 7 Days") : "Today"
  );

  useEffect(() => {
    if (fromDate && toDate) {
      const parsedStart = moment(fromDate, "DD/MM/YYYY");
      const parsedEnd = moment(toDate, "DD/MM/YYYY");
      setState({
        start: parsedStart,
        end: parsedEnd,
      });
      setLabelName(resolveRangeLabel(parsedStart, parsedEnd));
      return; // Skip default reset
    }
    const getPastDayDetails = () => {
      if (triggerDate) {
        const defaultStart = moment().subtract(7, "days");
        const defaultEnd = moment().subtract(1, "days");

        if (includeCurrentDate) {
          // default to today
          const today = moment();
          setState({ start: today, end: today });
          setLabelName("Today");
          handleStartDate(
            today.format("DD/MM/YYYY"),
            today.format("DD/MM/YYYY")
          );
        } else {
          setState({ start: defaultStart, end: defaultEnd });
          setLabelName("Last 7 Days");
          handleStartDate(
            defaultStart.format("DD/MM/YYYY"),
            defaultEnd.format("DD/MM/YYYY")
          );
        }
      } else {
        const today = moment();
        setState({ start: today, end: today });
        setLabelName(triggerDate ? "Last 7 Days" : "Today");
        handleStartDate(today.format("DD/MM/YYYY"), today.format("DD/MM/YYYY"));
      }
    };
    if (trigger) {
      getPastDayDetails();
      setTrigger(false);
    }
  }, [
    handleStartDate,
    includeCurrentDate,
    resolveRangeLabel,
    trigger,
    triggerDate,
    fromDate,
    toDate,
  ]);

  const { start, end } = state;
  const handleCallback = (start, end, rangeLabel) => {
    setState({ start, end });
    handleStartDate(
      moment(new Date(start)).format("DD/MM/YYYY"),
      moment(new Date(end)).format("DD/MM/YYYY")
    );
    setLabelName(rangeLabel);
  };
  const label = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");

  const getRanges = () => ({
    ...(showToday && {
      Today: [moment().toDate(), moment().toDate()],
    }),
    Yesterday: [
      moment().subtract(1, "days").toDate(),
      moment().subtract(1, "days").toDate(),
    ],
    "Last 7 Days": includeCurrentDate
      ? [moment().subtract(6, "days").toDate(), moment()]
      : [
          moment().subtract(7, "days").toDate(),
          moment().subtract(1, "days").toDate(),
        ],
    "Last 30 Days": [moment().subtract(30, "days").toDate(), moment().subtract(1, "days").toDate(),],
    "This Month": [
      moment().startOf("month").toDate(),
      moment().endOf("month").toDate(),
    ],
    "Last Month": [
      moment().subtract(1, "month").startOf("month").toDate(),
      moment().subtract(1, "month").endOf("month").toDate(),
    ],
  });

  return (
    <DateRangePicker
      initialSettings={{
        startDate: start.toDate(),
        endDate: end.toDate(),
        ranges: getRanges(),
        // maxDate: showToday
        //   ? moment().toDate()
        //   : moment().subtract(1, "days").toDate(),
        ...(allowFutureDate
          ? {}
          : {
              maxDate: showToday
                ? moment().toDate()
                : moment().subtract(1, "days").toDate(),
            }),
        applyClass: "custom-apply-btn",
        cancelClass: "custom-cancel-btn",
        locale: {
          format: "DD/MM/YYYY",
        },
      }}
      onCallback={handleCallback}
    >
      <TextField
        id="reportrange"
        label={labelName}
        placeholder={leaveManagamentPlaceholder.dateFormat}
        value={label}
        size="small"
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton>
                <DateRangeIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ shrink: true, required: true }}
        fullWidth
      />
    </DateRangePicker>
  );
};

export default BSDateRangePicker;
